import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, MenuItem, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import PageHeader from '../../../components/PageHeader/PageHeader';
import OverviewTab from './OverviewTab';
import CompaniesTab from './CompaniesTab';
import InfraTab from './InfraTab';
import CfeTab from './CfeTab';
import HealthTab from './HealthTab';
import AuditTab from './AuditTab';
import { MAX_LIVE_DAYS, clampLiveDays } from './opsText';
import { RANGE_OPTIONS, defaultGranularity, formatDateTime, formatDay, staleDays } from './reportsFormat';

const TABS = [
  { key: 'overview', label: 'Resumen' },
  { key: 'companies', label: 'Empresas' },
  { key: 'infra', label: 'Infraestructura' },
  { key: 'cfe', label: 'Facturación (CFE)' },
  { key: 'health', label: 'Salud operativa' },
  { key: 'audit', label: 'Auditoría' },
];

// Salud y auditoría se leen en vivo y guardan como mucho 90 días.
const LIVE_TABS = ['health', 'audit'];

const GRANULARITIES = [
  { value: 'day', label: 'Por día' },
  { value: 'week', label: 'Por semana' },
  { value: 'month', label: 'Por mes' },
];

/** Reportes del superadmin. Todo sale de la foto que arma el job nocturno: nunca se consultan las bases de las empresas. */
export default function ReportsPage() {
  const [tab, setTab] = useState('overview');
  const [days, setDays] = useState(30);
  const [granularity, setGranularity] = useState(null); // null = automática según el rango
  const [freshness, setFreshness] = useState(null);

  const effectiveGranularity = granularity || defaultGranularity(days);
  const live = LIVE_TABS.includes(tab);
  const usesRange = tab !== 'companies';
  const rangeOptions = live ? RANGE_OPTIONS.filter((o) => o.value <= MAX_LIVE_DAYS) : RANGE_OPTIONS;
  const onFreshness = useCallback((f) => setFreshness(f), []);
  const stale = freshness?.live ? null : staleDays(freshness?.dataAsOf);
  const effectiveDays = live ? clampLiveDays(days) : days;

  // Al pasar a una pestaña en vivo con un rango mayor a 90 días, el rango baja a 90 (el selector también deja de ofrecerlo).
  useEffect(() => { if (live && days > MAX_LIVE_DAYS) setDays(MAX_LIVE_DAYS); }, [live, days]);

  return (
    <Box p={3}>
      {/* Título a la izquierda; período, agrupación y hora de los datos arriba a la derecha. */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { md: 'flex-start' }, gap: 2 }}>
        <PageHeader title="Reportes" subtitle="Uso del sistema, empresas, infraestructura y facturación" />

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', md: 'flex-end' }, gap: 1 }}>
          <Stack direction="row" spacing={2}>
            {usesRange && (
              <TextField size="small" select label="Período" value={effectiveDays} onChange={(e) => setDays(Number(e.target.value))} sx={{ minWidth: 190 }}>
                {rangeOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            )}
            {tab === 'overview' && (
              <TextField size="small" select label="Agrupar" value={effectiveGranularity} onChange={(e) => setGranularity(e.target.value)} sx={{ minWidth: 150 }}>
                {GRANULARITIES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            )}
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: { md: 'right' } }}>
            {freshness?.live
              ? `En vivo · actualizado ${formatDateTime(freshness.generatedAt)}`
              : freshness?.dataAsOf
                ? `Datos al ${formatDay(freshness.dataAsOf)} · procesados ${formatDateTime(freshness.computedAt)}`
                : 'Los datos se procesan cada noche a las 4:30'}
          </Typography>
        </Box>
      </Box>

      {freshness && !freshness.live && !freshness.dataAsOf && (
        <Alert severity="info" sx={{ mb: 2 }}>Todavía no hay datos: la primera foto se arma esta noche a las 4:30.</Alert>
      )}
      {stale !== null && stale >= 2 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Los datos tienen {stale} días de atraso: es posible que el proceso nocturno haya fallado. Lo que ves es la última foto disponible.
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        {TABS.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
      </Tabs>

      {tab === 'overview' && <OverviewTab days={days} granularity={effectiveGranularity} onFreshness={onFreshness} />}
      {tab === 'companies' && <CompaniesTab onFreshness={onFreshness} />}
      {tab === 'infra' && <InfraTab days={days} onFreshness={onFreshness} />}
      {tab === 'cfe' && <CfeTab days={days} onFreshness={onFreshness} />}
      {tab === 'health' && <HealthTab days={effectiveDays} onFreshness={onFreshness} />}
      {tab === 'audit' && <AuditTab days={effectiveDays} onFreshness={onFreshness} />}
    </Box>
  );
}
